import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { lsGet, lsSet } from '../lib/kv';
import { lookupFr } from './legacyFr';
import { en, fr, format, type StringKey, type Vars } from './strings';

export type Lang = 'en' | 'fr';

export interface I18n {
  lang: Lang;
  setLang(l: Lang): void;
  /** keyed interface text */
  t(key: StringKey, vars?: Vars): string;
  /** an English text of the Manifest / Program builder, in French when FR is on */
  tr(english: string): string;
  locale: string;
  fmtNum(n: number | null | undefined, dec?: number): string;
  fmtDate(v: string | Date | null | undefined, opts?: Intl.DateTimeFormatOptions): string;
}

function initialLang(): Lang {
  const saved = lsGet('uco.lang');
  if (saved === 'fr' || saved === 'en') return saved;
  return /^fr/i.test(navigator.language || '') ? 'fr' : 'en';
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const setLang = useCallback((l: Lang) => {
    lsSet('uco.lang', l);
    setLangState(l);
  }, []);
  const value = useMemo<I18n>(() => {
    const table = lang === 'fr' ? fr : en;
    const locale = lang === 'fr' ? 'fr-FR' : 'en-GB';
    return {
      lang,
      setLang,
      locale,
      t: (key, vars) => format(table[key] ?? en[key] ?? key, vars),
      tr: (s) => {
        if (lang !== 'fr') return s;
        const f = lookupFr(s);
        return f === null ? s : f;
      },
      fmtNum: (n, dec) => (Number(n) || 0).toLocaleString(locale, { maximumFractionDigits: dec === undefined ? 0 : dec, minimumFractionDigits: 0 }),
      fmtDate: (v, opts) => {
        if (!v) return '—';
        const d = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v + 'T12:00:00') : new Date(v);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString(locale, opts || { day: '2-digit', month: 'short', year: 'numeric' });
      },
    };
  }, [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}
